import { getCssVariable } from "@/utils/colorConverter";
import { type SalesMetricV2 } from "@north-beam/nb-client-side/metrics-v2";
import {
  type IMCAttributionWindowDays,
  type INBAccountingMode,
} from "@north-beam/nb-common";

import { type MetricData, type TileDescriptor } from "../../types";

export type MetricsExplorerCorrelationRow = {
  r: number;
  canBeCalculated: boolean;
};

export function buildAnalysisRows({
  metricsData,
  tiles,
  attributionModel,
  attributionWindow,
  accountingMode,
  buildMetrics,
}: {
  metricsData: MetricData[];
  tiles: TileDescriptor[];
  attributionModel: string;
  attributionWindow: IMCAttributionWindowDays;
  accountingMode: INBAccountingMode;
  buildMetrics(value: {
    attributionModel: string;
    attributionWindow: IMCAttributionWindowDays;
    accountingMode: INBAccountingMode;
  }): SalesMetricV2[];
}) {
  const rowsByTileId: Record<
    string,
    { tile: TileDescriptor; metric: SalesMetricV2; values: number[] }
  > = {};

  metricsData.forEach((data) => {
    const tile = tiles.find((e) => e.id === data.tileId);

    if (data.status === "loading") {
      return;
    }

    if (!tile) {
      return;
    }

    const metrics = buildMetrics({
      attributionModel: tile.attributionModel ?? attributionModel,
      attributionWindow: tile.attributionWindow ?? attributionWindow,
      accountingMode: tile.accountingMode ?? accountingMode,
    });

    const metric = metrics.find((e) => e.id === tile.metricId);

    if (!metric) {
      return;
    }

    const values: number[] = [];

    data.data?.rows.forEach((row) => {
      const value = metric.getValue(row.metrics) ?? 0;
      let y = isNaN(value) ? 0 : value ?? 0;

      if (Math.abs(y) < 1e-6 || y === Infinity) {
        y = 0;
      }

      values.push(y);
    });

    rowsByTileId[tile.id] = { tile, metric, values };
  });

  return rowsByTileId;
}

export function getSentence(r: number) {
  if (r > 0.7) {
    return "VERY STRONG POSITIVE";
  }

  if (r > 0.5) {
    return "STRONG POSITIVE";
  }

  if (r > 0.3) {
    return "MODERATE POSITIVE";
  }

  if (r > 0.1) {
    return "WEAK POSITIVE";
  }

  if (r >= -0.1) {
    return "NO RELATIONSHIP";
  }

  if (r >= -0.3) {
    return "WEAK NEGATIVE";
  }

  if (r >= -0.5) {
    return "MODERATE NEGATIVE";
  }

  if (r >= -0.7) {
    return "STRONG NEGATIVE";
  }

  return "VERY STRONG NEGATIVE";
}

export function getColors(r: number) {
  const veryStrongNegative = getCssVariable("--v2-very-strong-negative");
  const strongNegative = getCssVariable("--v2-strong-negative");
  const moderateNegative = getCssVariable("--v2-moderate-negative");
  const weakNegative = getCssVariable("--v2-weak-negative");
  const weakNegativeText = getCssVariable("--v2-action-negative");
  const noRelationship = getCssVariable("--v2-no-relationship");
  const noRelationshipText = getCssVariable("--v2-text-secondary");
  const weakPositive = getCssVariable("--v2-weak-positive");
  const weakPositiveText = getCssVariable("--v2-action-positive");
  const moderatePositive = getCssVariable("--v2-moderate-positive");
  const strongPositive = getCssVariable("--v2-strong-positive");
  const veryStrongPositive = getCssVariable("--v2-very-strong-positive");

  let bgColor = veryStrongNegative;
  let textColor = "#FFFFFF";

  if (r > 0.7) {
    bgColor = veryStrongPositive;
    textColor = "#FFFFFF";
  } else if (r > 0.5) {
    bgColor = strongPositive;
    textColor = "#FFFFFF";
  } else if (r > 0.3) {
    bgColor = moderatePositive;
    textColor = "#FFFFFF";
  } else if (r > 0.1) {
    bgColor = weakPositive;
    textColor = weakPositiveText;
  } else if (r >= -0.1) {
    bgColor = noRelationship;
    textColor = noRelationshipText;
  } else if (r >= -0.3) {
    bgColor = weakNegative;
    textColor = weakNegativeText;
  } else if (r >= -0.5) {
    bgColor = moderateNegative;
    textColor = "#FFFFFF";
  } else if (r >= -0.7) {
    bgColor = strongNegative;
    textColor = "#FFFFFF";
  }

  return { bgColor, textColor };
}

export function getTooltipContent(r: number) {
  if (r > 0.7) {
    return "Both metrics almost always rise together in sync.";
  }

  if (r > 0.5) {
    return "When one metric goes up, the other usually increases too.";
  }

  if (r > 0.3) {
    return "As one metric rises, the other tends to increase, though not always.";
  }

  if (r > 0.1) {
    return "There’s a slight tendency for both metrics to increase together.";
  }

  if (r >= -0.1) {
    return "Changes in one metric don't predict changes in the other.";
  }

  if (r >= -0.3) {
    return "There’s a loose pattern where one metric decreases slightly as the other increases.";
  }

  if (r >= -0.5) {
    return "There's a noticeable tendency for one metric to decrease as the other increases.";
  }

  if (r >= -0.7) {
    return "When one metric goes up, the other tends to go down consistently.";
  }

  return "As one metric increases, the other almost always decreases significantly.";
}
